/**
 * 使い方ガイド（画面をまたぐガイド）の目印
 * ガイドが指し示す実画面の要素へ付けるdata属性を定義します。
 * ScreenLayout（shared）も「？」ボタンへ付けるため shared に置き、features → shared の向きを保ちます。
 * 目印と手順の食い違いは visitorGuidedTourAnchors.contract.test.js がソースを走査して双方向で落とします。
 */

/** 目印を表すdata属性名。ガイド本体はこの属性だけでDOMを探す（testIDやaria-labelには依存しない） */
export const GUIDED_TOUR_ANCHOR_ATTRIBUTE = 'data-tour-anchor';

/**
 * ガイドが参照する目印IDの一覧
 * - tabBar は暗幕の下端を決めるために測るだけで、指し示さない（下部タブは常に明るく操作できる）
 * - tab.* は画面が変わる手順で「次に押すタブ」として金の枠で囲む
 */
export const GUIDED_TOUR_ANCHOR_IDS = Object.freeze([
  'tabBar',
  'tab.home',
  'tab.scan',
  'tab.rewards',
  'tab.surveys',
  'screen.help',
  'home.points',
  'home.stampBook',
  'home.guideMenu',
  'scan.camera',
  'scan.manualCode',
  'rewards.catalog',
  'rewards.history',
  'surveys.list',
] as const);

/** ガイドが指し示す要素のID */
export type GuidedTourAnchorId = (typeof GUIDED_TOUR_ANCHOR_IDS)[number];

/** 要素へ展開する目印属性 */
export type GuidedTourAnchorProps = Readonly<{ 'data-tour-anchor': GuidedTourAnchorId }>;

/**
 * 要素へ展開する目印属性を返す
 * WebPrimitives の View / Pressable / TextInput は data-* をそのままDOMへ渡すため、展開するだけで目印になる。
 * @param {GuidedTourAnchorId} anchorId - 目印ID
 * @returns {GuidedTourAnchorProps} data属性
 */
export const tourAnchor = (anchorId: GuidedTourAnchorId): GuidedTourAnchorProps => ({
  [GUIDED_TOUR_ANCHOR_ATTRIBUTE]: anchorId,
});

/**
 * 目印IDから、要素を探すCSSセレクタを作る
 * @param {GuidedTourAnchorId} anchorId - 目印ID
 * @returns {string} CSSセレクタ
 */
export const buildTourAnchorSelector = (anchorId: GuidedTourAnchorId): string =>
  `[${GUIDED_TOUR_ANCHOR_ATTRIBUTE}="${anchorId}"]`;
